/**
 * «Задачи робота» (`/voice-lab?tab=problems`): вопросы, которые голосовой робот передал коллеге (`report_problem`) —
 * не смог ответить или клиентка недовольна. Сотрудник звонит и отмечает «Сделано». До 27.10.26 жила в «Телефонии».
 * Спека `docs/specs/2026-10-27-telephony-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { closeProblem, problemsQuery } from "@/lib/telephony";
import type { CallProblem } from "@/lib/voiceLab";

const ВИД: Record<string, string> = { no_answer: "не смог ответить", complaint: "жалоба", other: "другое" };

function Задача({ p }: { p: CallProblem }) {
  const qc = useQueryClient();
  const [комментарий, setКомментарий] = useState(p.comment ?? "");
  const m = useMutation({
    mutationFn: (done: boolean) => closeProblem(p.id, { done, comment: комментарий || null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["telephony-problems"] }),
  });
  return (
    <div className="flex flex-col gap-1.5 border-b border-border py-3">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <b>{ВИД[p.kind] ?? p.kind}</b>
        <span className="text-fg-muted">
          {p.date?.slice(5, 16)} · {p.phone ? `+${p.phone}` : "номера нет"}
        </span>
        {p.room && (
          <a href={`/voice-lab?tab=calls&call=${p.room}`} className="text-accent hover:underline">
            звонок ↗
          </a>
        )}
        <span className={p.status === "done" ? "text-ok" : "text-err"}>{p.status === "done" ? "сделано" : "ждёт коллегу"}</span>
      </div>
      <div className="text-[13px]">{p.summary}</div>
      {!!p.context.length && (
        <details className="text-fg-muted">
          <summary className="cursor-pointer">разговор до этого ({p.context.length})</summary>
          {p.context.map((t, i) => (
            <div key={i}>
              <span className="text-fg-faint">{t.who === "agent" ? "робот" : "клиентка"}:</span> {t.text}
            </div>
          ))}
        </details>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={комментарий}
          onChange={(e) => setКомментарий(e.target.value)}
          placeholder="что сделали"
          className="h-8 min-w-0 flex-1 rounded-[var(--radius-ui)] border border-border bg-transparent px-2 text-[12.5px]"
        />
        <button
          type="button"
          disabled={m.isPending}
          onClick={() => m.mutate(p.status !== "done")}
          className="h-8 rounded-[var(--radius-ui)] border border-accent px-3 text-accent disabled:opacity-50"
        >
          {p.status === "done" ? "Вернуть в работу" : "Сделано"}
        </button>
      </div>
    </div>
  );
}

export function VoiceLabProblems() {
  const [статус, setСтатус] = useState<"new" | "done" | null>("new");
  const q = useQuery({ ...problemsQuery(статус), refetchInterval: 30000 });
  const строки = q.data?.rows ?? [];
  return (
    <div className="flex max-w-4xl flex-col gap-2">
      <div className="text-fg-muted">
        Голосовой робот не смог ответить клиентке или она недовольна разговором — он извинился и пообещал, что
        свяжется коллега. Позвоните, отметьте «Сделано» с комментарием.
      </div>
      <div className="flex gap-1.5">
        {(
          [
            ["new", "Ждут"],
            ["done", "Сделано"],
            [null, "Все"],
          ] as const
        ).map(([к, имя]) => (
          <button
            key={имя}
            type="button"
            onClick={() => setСтатус(к)}
            className={`rounded-full border px-2.5 py-0.5 text-[12px] ${статус === к ? "border-accent text-accent" : "border-border text-fg-muted"}`}
          >
            {имя}
          </button>
        ))}
      </div>
      {строки.map((p) => (
        <Задача key={p.id} p={p} />
      ))}
      {!q.isLoading && !строки.length && <div className="text-fg-muted">Задач нет</div>}
    </div>
  );
}
